import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router';
import type { ModelComparisonParams, ModelProvider } from '../../../api/agent-miner-client/agent-miner-client.type';
import type { TimeAllocationType } from '../../../api/work-periods-client/work-periods-client.type';
import { formatToISODate } from '../../../helpers/date-time-helpers';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { parseIsoDate, readFiltersFromSearch, resolveStartEnd } from '../sessions-list/filters/session-filters.helpers';
import { parseAllocationType, resolveEffectiveAllocationType } from './model-comparison.helpers';
import { MODEL_PROVIDER_VALUES } from './model-comparison.type';
import type { CycleView, ModelComparisonView, SessionsDrilldownWindow } from './model-comparison.type';

const DEFAULT_WINDOW_DAYS = 30;

/**
 * Resolves the model comparison query params from the global organization and
 * the tab's URL date window (the same `started`/`start_date`/`end_date`
 * params the Sessions tab uses, so switching tabs preserves the range).
 * Defaults to the trailing 30 days when the URL carries no usable window — this
 * tab is window-scoped, so a carried-over "All time" preset falls back here too.
 *
 * The effective `time_allocation_type` is always included: an explicit
 * `allocation` override when set, otherwise the window-derived default (which
 * matches what the backend would pick), so the value is part of the query key
 * and refetches when the user changes bucket size.
 *
 * @returns {ModelComparisonParams} Query params for useModelComparison
 */
const useModelComparisonParams = (): ModelComparisonParams => {
  const [searchParams] = useSearchParams();
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const allocationOverride = searchParams.get('allocation');

  return useMemo(() => {
    const { start, end } = resolveStartEnd(readFiltersFromSearch(searchParams));
    const endDate = end ?? formatToISODate(new Date());
    // end_date is inclusive on the backend, so a true 30-day window starts 29
    // days back — subtracting the full 30 would span 31 calendar days. Matches
    // the corrected "Last 30 days" preset in resolveStartEnd.
    const startDate = start ?? formatToISODate(dayjs(endDate).subtract(DEFAULT_WINDOW_DAYS - 1, 'day'));
    return {
      organization_id: organizationId ?? '',
      start_date: startDate,
      end_date: endDate,
      time_allocation_type: resolveEffectiveAllocationType(parseAllocationType(allocationOverride), startDate, endDate),
    };
  }, [searchParams, organizationId, allocationOverride]);
};

/**
 * URL-synced allocation-type control for the Model Comparison sidebar. The
 * `override` reflects an explicit user choice (the `allocation` param);
 * `effectiveAllocationType` is what's actually in effect (override or the
 * window-derived default) and is what the Select displays, mirroring
 * process-analysis's allocation filter.
 *
 * @returns {{ override: TimeAllocationType | null, effectiveAllocationType: TimeAllocationType, setAllocationType: (next: TimeAllocationType) => void }}
 */
const useModelComparisonAllocationType = (): {
  override: TimeAllocationType | null;
  effectiveAllocationType: TimeAllocationType;
  setAllocationType: (next: TimeAllocationType) => void;
} => {
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useModelComparisonParams();
  const override = parseAllocationType(searchParams.get('allocation'));

  const setAllocationType = (next: TimeAllocationType) => {
    const copy = new URLSearchParams(searchParams);
    copy.set('allocation', next);
    setSearchParams(copy, { replace: true });
  };

  // params.time_allocation_type is always the effective value (see above).
  return {
    override,
    effectiveAllocationType: params.time_allocation_type as TimeAllocationType,
    setAllocationType,
  };
};

/**
 * Chart/table view mode for the tab, kept in the `view` URL param so it
 * survives navigation like the rest of the page's filter state.
 *
 * @returns {{ view: ModelComparisonView, setView: (view: ModelComparisonView) => void }}
 */
const useModelComparisonView = (): {
  view: ModelComparisonView;
  setView: (view: ModelComparisonView) => void;
} => {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get('view');
  const view: ModelComparisonView = raw === 'table' || raw === 'usage' ? raw : 'chart';

  const setView = (next: ModelComparisonView) => {
    const copy = new URLSearchParams(searchParams);
    if (next === 'chart') copy.delete('view');
    else copy.set('view', next);
    setSearchParams(copy, { replace: true });
  };

  return { view, setView };
};

/**
 * URL-synced line/bar toggle for the cycle-time chart, held in the `cycle_view`
 * param. Rendered in the chart header (not the sidebar), independent of the
 * sidebar `view` selector that chose the cycle-time chart in the first place.
 * Defaults to the line chart, omitting the param entirely so shared URLs stay
 * clean; an unknown hand-edited value falls back to the line chart.
 *
 * @returns {{ cycleView: CycleView, setCycleView: (next: CycleView) => void }}
 */
const useCycleView = (): {
  cycleView: CycleView;
  setCycleView: (next: CycleView) => void;
} => {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get('cycle_view');
  const cycleView: CycleView = raw === 'bar' ? 'bar' : 'line';

  const setCycleView = (next: CycleView) => {
    const copy = new URLSearchParams(searchParams);
    if (next === 'line') copy.delete('cycle_view');
    else copy.set('cycle_view', next);
    setSearchParams(copy, { replace: true });
  };

  return { cycleView, setCycleView };
};

/**
 * URL-synced provider filter for the Model Comparison tab. Reads/writes a
 * comma-separated `model_provider` param, validated against known
 * {@link ModelProvider} values so a hand-edited URL can't inject garbage.
 * Values are deduplicated and lowercased before validation, so a repeated or
 * differently-cased token in a hand-edited URL doesn't produce duplicate
 * pills or get silently dropped. Filtering happens entirely client-side over
 * the already-fetched response (see {@link filterResponseByProviders}), so
 * this doesn't affect the query key — an empty selection means "no filter".
 *
 * @returns {{ providers: ModelProvider[], setProviders: (next: ModelProvider[]) => void }}
 */
const useModelComparisonProviderFilter = (): {
  providers: ModelProvider[];
  setProviders: (next: ModelProvider[]) => void;
} => {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get('model_provider');

  const providers = useMemo(() => {
    const tokens = raw ? raw.split(',').filter(Boolean) : [];
    const deduped = Array.from(new Set(tokens.map((token) => token.toLowerCase())));
    return deduped.filter((value): value is ModelProvider => MODEL_PROVIDER_VALUES.has(value));
  }, [raw]);

  const setProviders = (next: ModelProvider[]) => {
    const copy = new URLSearchParams(searchParams);
    if (next.length === 0) copy.delete('model_provider');
    else copy.set('model_provider', next.join(','));
    setSearchParams(copy, { replace: true });
  };

  return { providers, setProviders };
};

/**
 * URL-synced toggle for whether the unattributable "Unknown" model family is
 * shown, held in the `model_show_unknown` param. The Unknown family is HIDDEN
 * by default, so the ABSENCE of the param means hidden and a clean/shared URL
 * hides it; only `model_show_unknown=1` reveals it. Any other value (a
 * hand-edited URL) is treated as "not 1" and therefore hidden, so the param
 * can't inject a third state.
 *
 * @returns {{ showUnknown: boolean, setShowUnknown: (next: boolean) => void }}
 */
const useModelComparisonShowUnknown = (): {
  showUnknown: boolean;
  setShowUnknown: (next: boolean) => void;
} => {
  const [searchParams, setSearchParams] = useSearchParams();
  const showUnknown = searchParams.get('model_show_unknown') === '1';

  const setShowUnknown = (next: boolean) => {
    const copy = new URLSearchParams(searchParams);
    if (next) copy.set('model_show_unknown', '1');
    else copy.delete('model_show_unknown');
    setSearchParams(copy, { replace: true });
  };

  return { showUnknown, setShowUnknown };
};

/**
 * URL-synced selection of the model family whose sessions the side panel is
 * listing, held in the `sessions_family` param so the drill-down survives a
 * refresh and is shareable like the rest of the tab's state.
 *
 * The value is a free-form display label rather than an enum: the backend
 * derives families from raw model ids and passes unmatched ids through
 * verbatim, so a newly-released model must be selectable without a deploy.
 *
 * @returns {{ family: string | null, setFamily: (next: string | null) => void }}
 */
const useModelComparisonSessionsFamily = (): {
  family: string | null;
  window: SessionsDrilldownWindow | null;
  setFamily: (next: string | null, window?: SessionsDrilldownWindow | null) => void;
} => {
  const [searchParams, setSearchParams] = useSearchParams();
  const family = searchParams.get('sessions_family') || null;
  const rawStart = searchParams.get('sessions_start');
  const rawEnd = searchParams.get('sessions_end');

  // Both bounds must be present and well-formed; otherwise the drill-down
  // falls back to the tab's own window rather than sending a half-open range
  // to the API. Validated because a hand-edited URL feeds a date filter.
  const window = useMemo(() => {
    const start = parseIsoDate(rawStart);
    const end = parseIsoDate(rawEnd);
    return start && end ? { start, end } : null;
  }, [rawStart, rawEnd]);

  // Family and window are written in ONE setSearchParams call — splitting them
  // across two URL-backed setters would race off the same stale snapshot and
  // only the last write would survive (see handleResetFilters in
  // model-comparison.tsx for the same hazard).
  const setFamily = (next: string | null, nextWindow?: SessionsDrilldownWindow | null) => {
    const copy = new URLSearchParams(searchParams);
    if (next) copy.set('sessions_family', next);
    else copy.delete('sessions_family');
    // No window means "the tab's whole range" (a table row click), so the
    // bounds are cleared rather than left over from a previous dot click.
    if (next && nextWindow) {
      copy.set('sessions_start', nextWindow.start);
      copy.set('sessions_end', nextWindow.end);
    } else {
      copy.delete('sessions_start');
      copy.delete('sessions_end');
    }
    setSearchParams(copy, { replace: true });
  };

  return { family, window, setFamily };
};

/**
 * URL-synced set of model families whose chart lines are hidden, held in a
 * comma-separated `hidden_families` param so the chart and the filter panel's
 * select/deselect-all control share one source of truth (they render in
 * different PageLayout slots and can't share component state). Values are
 * free-form display labels for the same reason as `sessions_family`: the
 * backend passes unmatched model ids through verbatim.
 *
 * @returns {{ hiddenFamilies: Set<string>, toggleFamily: (family: string) => void, setHiddenFamilies: (next: Set<string>) => void }}
 */
const useModelComparisonHiddenFamilies = (): {
  hiddenFamilies: Set<string>;
  toggleFamily: (family: string) => void;
  setHiddenFamilies: (next: Set<string>) => void;
} => {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get('hidden_families');

  const hiddenFamilies = useMemo(() => new Set((raw ? raw.split(',') : []).filter(Boolean)), [raw]);

  const setHiddenFamilies = (next: Set<string>) => {
    const copy = new URLSearchParams(searchParams);
    if (next.size === 0) copy.delete('hidden_families');
    else copy.set('hidden_families', Array.from(next).join(','));
    setSearchParams(copy, { replace: true });
  };

  const toggleFamily = (family: string) => {
    const next = new Set(hiddenFamilies);
    if (next.has(family)) next.delete(family);
    else next.add(family);
    setHiddenFamilies(next);
  };

  return { hiddenFamilies, toggleFamily, setHiddenFamilies };
};

export {
  useCycleView,
  useModelComparisonAllocationType,
  useModelComparisonHiddenFamilies,
  useModelComparisonParams,
  useModelComparisonProviderFilter,
  useModelComparisonSessionsFamily,
  useModelComparisonShowUnknown,
  useModelComparisonView,
};
